/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getTimeFormatSettings,
  getEffectiveTimezone,
  getBrowserTimezone,
} from "@/utils/timeSettings";
import { getWeather } from "@/api/weather";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

type IconKind = "clear" | "partly" | "cloud" | "fog" | "drizzle" | "rain" | "snow" | "thunder";

// WMO weather interpretation codes -> a coarse icon bucket + label.
export function classify(code: number): { kind: IconKind; label: string } {
  if (code === 0) return { kind: "clear", label: "Clear" };
  if (code === 1) return { kind: "partly", label: "Mainly clear" };
  if (code === 2) return { kind: "partly", label: "Partly cloudy" };
  if (code === 3) return { kind: "cloud", label: "Overcast" };
  if (code === 45 || code === 48) return { kind: "fog", label: "Fog" };
  if (code >= 51 && code <= 57) return { kind: "drizzle", label: "Drizzle" };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return { kind: "rain", label: "Rain" };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { kind: "snow", label: "Snow" };
  if (code >= 95) return { kind: "thunder", label: "Thunderstorm" };
  return { kind: "cloud", label: "Cloudy" };
}

export const WeatherIcon: React.FC<{ kind: IconKind; className?: string }> = ({ kind, className }) => {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
  };
  switch (kind) {
    case "clear":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
        </svg>
      );
    case "partly":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <circle cx="8" cy="8" r="3" />
          <path d="M8 2v1.5M2 8h1.5M4 4l1 1M12.5 4l-1 1" />
          <path d="M9 18h8a3 3 0 0 0 .3-5.98A4.2 4.2 0 0 0 9.5 11 3.5 3.5 0 0 0 9 18Z" />
        </svg>
      );
    case "fog":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <path d="M6 14h11a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 6.2 6 3.7 3.7 0 0 0 6 14Z" />
          <path d="M4 18h13M6 21h11" />
        </svg>
      );
    case "drizzle":
    case "rain":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <path d="M7 15h9a3.4 3.4 0 0 0 .3-6.8A4.9 4.9 0 0 0 7.2 6 3.9 3.9 0 0 0 7 15Z" />
          <path d="M8 18l-1 2.5M12 18l-1 2.5M16 18l-1 2.5" />
        </svg>
      );
    case "snow":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <path d="M7 15h9a3.4 3.4 0 0 0 .3-6.8A4.9 4.9 0 0 0 7.2 6 3.9 3.9 0 0 0 7 15Z" />
          <path d="M8 19h.01M12 20h.01M16 19h.01" />
        </svg>
      );
    case "thunder":
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <path d="M7 14h9a3.4 3.4 0 0 0 .3-6.8A4.9 4.9 0 0 0 7.2 5 3.9 3.9 0 0 0 7 14Z" />
          <path d="M12 13l-2 4h3l-2 4" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className={className} {...common}>
          <path d="M7 17h9a3.5 3.5 0 0 0 .3-6.98A5 5 0 0 0 7.2 8.4 4 4 0 0 0 7 17Z" />
        </svg>
      );
  }
};

const TONE: Record<IconKind, string> = {
  clear: "text-amber-500",
  partly: "text-amber-500",
  cloud: "text-slate-400",
  fog: "text-slate-400",
  drizzle: "text-sky-500",
  rain: "text-sky-500",
  snow: "text-cyan-400",
  thunder: "text-violet-400",
};

/**
 * One-line clock for the top right of every desktop page: time down to the
 * millisecond, the date and the weather. The digits are written straight into
 * the DOM from one animation frame loop (a React render per frame would be a
 * waste), the date and the hours/minutes change when the clock does, and the
 * temperature glides to a new reading. Digits are tabular, so nothing shifts.
 */
const DesktopClock: React.FC = () => {
  const [settings, setSettings] = useState(() => getTimeFormatSettings());
  useEffect(() => {
    const onChange = () => setSettings(getTimeFormatSettings());
    window.addEventListener("timeSettingsChanged", onChange);
    return () => window.removeEventListener("timeSettingsChanged", onChange);
  }, []);
  const tz = getEffectiveTimezone(settings);

  const { data: wx, isError: wxError } = useQuery({
    queryKey: ["weather"],
    queryFn: getWeather,
    refetchInterval: 15 * 60_000,
    retry: false,
  });

  const fmt = useMemo(
    () => ({
      time: new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz }),
      date: new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: tz }),
    }),
    [tz],
  );
  const initial = useMemo(() => {
    const d = new Date();
    const parts = Object.fromEntries(fmt.time.formatToParts(d).map((p) => [p.type, p.value]));
    return { hm: `${parts.hour}:${parts.minute}`, ss: `:${parts.second}`, date: fmt.date.format(d) };
  }, [fmt]);

  const hmRef = useRef<HTMLSpanElement>(null);
  const ssRef = useRef<HTMLSpanElement>(null);
  const msRef = useRef<HTMLSpanElement>(null);
  const dateRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let raf = 0;
    let lastSecond = -1;
    let lastDate = "";
    const tick = () => {
      const now = Date.now();
      const sec = Math.floor(now / 1000);
      if (sec !== lastSecond) {
        lastSecond = sec;
        const d = new Date(now);
        const parts = Object.fromEntries(fmt.time.formatToParts(d).map((p) => [p.type, p.value]));
        if (hmRef.current) hmRef.current.textContent = `${parts.hour}:${parts.minute}`;
        if (ssRef.current) ssRef.current.textContent = `:${parts.second}`;
        const date = fmt.date.format(d);
        if (date !== lastDate && dateRef.current) {
          lastDate = date;
          dateRef.current.textContent = date;
        }
      }
      if (msRef.current) msRef.current.textContent = `.${String(now % 1000).padStart(3, "0")}`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fmt]);

  const deg = wx?.units === "fahrenheit" ? "°F" : "°C";
  const cur = wx ? classify(wx.weatherCode) : null;
  const offset = new Date().toLocaleTimeString("en-US", { timeZoneName: "shortOffset", timeZone: tz }).split(" ").pop();
  const today = wx?.daily?.[0];

  return (
    <div
      className="flex h-12 flex-none items-center gap-3.5 whitespace-nowrap rounded-lg border border-gray-300 bg-transparent px-4 tabular-nums dark:border-white/20"
      title={`${tz ?? getBrowserTimezone()}${offset ? ` · ${offset}` : ""}`}
    >
      <span className="flex items-baseline leading-none text-gray-900 dark:text-white">
        <span ref={hmRef} className="text-[24px] font-semibold tracking-tight">{initial.hm}</span>
        <span ref={ssRef} className="text-[15px] font-medium text-emerald-600 dark:text-emerald-400">{initial.ss}</span>
        <span ref={msRef} className="w-[2.3em] text-[12px] text-gray-400 dark:text-gray-500">.000</span>
      </span>

      <span className="h-6 w-px bg-gray-300 dark:bg-white/20" aria-hidden="true" />
      <span ref={dateRef} className="text-[13px] capitalize text-gray-600 dark:text-gray-300">{initial.date}</span>

      <span className="h-6 w-px bg-gray-300 dark:bg-white/20" aria-hidden="true" />
      {wx && cur ? (
        <span
          className="flex items-center gap-2"
          title={`${wx.condition || cur.label} · ${wx.location} — feels ${Math.round(wx.apparentTemperature)}${deg} · hum ${Math.round(wx.humidity)}% · wind ${Math.round(wx.windSpeed)} km/h`}
        >
          <WeatherIcon kind={cur.kind} className={`h-6 w-6 flex-none ${TONE[cur.kind]}`} />
          <span className="text-[19px] font-semibold text-gray-900 dark:text-white">
            <AnimatedNumber value={wx.temperature} mountFrom={null} format={(n) => `${Math.round(n)}${deg}`} />
          </span>
          <span className="hidden text-[12px] text-gray-500 xl:inline dark:text-gray-400">
            {wx.condition || cur.label}
            {today ? ` · ${Math.round(today.tempMin)}…${Math.round(today.tempMax)}${deg}` : ""}
          </span>
        </span>
      ) : wxError ? (
        <span className="text-[12px] text-gray-400">weather unavailable</span>
      ) : (
        <button
          type="button"
          className="text-[12px] font-medium text-blue-500 hover:underline"
          onClick={() => window.dispatchEvent(new CustomEvent("nyrvex:open-settings", { detail: { section: "weather" } }))}
        >
          + weather
        </button>
      )}
    </div>
  );
};

/**
 * Time + weather strip pinned to the top of every page (desktop), and a
 * compact variant for the mobile top bar. Honours the app's time-format
 * setting (24h toggle + timezone); weather comes from the Open-Meteo proxy for
 * the location set in Settings -> Weather.
 */
export const TopInfo: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  if (!compact) return <DesktopClock />;
  return <CompactClock />;
};

const CompactClock: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  const [settings, setSettings] = useState(() => getTimeFormatSettings());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 1000);
    const onChange = () => setSettings(getTimeFormatSettings());
    window.addEventListener("timeSettingsChanged", onChange);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener("timeSettingsChanged", onChange);
    };
  }, []);

  const { data: wx } = useQuery({
    queryKey: ["weather"],
    queryFn: getWeather,
    refetchInterval: 15 * 60_000,
    retry: false,
  });

  const tz = getEffectiveTimezone(settings);
  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: tz });
  const deg = wx?.units === "fahrenheit" ? "°F" : "°C";
  const cur = wx ? classify(wx.weatherCode) : null;

  return (
    <div className="flex flex-none items-center gap-2 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] tabular-nums leading-none text-gray-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-300">
      <span className="font-semibold text-gray-900 dark:text-white">{time}</span>
      {wx && cur && (
        <>
          <span className="h-3 w-px bg-gray-200 dark:bg-white/15" />
          <span className="flex items-center gap-1">
            <WeatherIcon kind={cur.kind} className="h-4 w-4" />
            {Math.round(wx.temperature)}
            {deg}
          </span>
        </>
      )}
    </div>
  );
};
